/**
 * Countries, currencies and number formats, for the places a restaurant says
 * where it is: Settings, and opening a restaurant on the platform. One copy, so
 * the two never offer different lists. Names come from the browser
 * (`Intl.DisplayNames`), in the reader's own language.
 */

/** ISO 3166-1 alpha-2, every assigned code. Names come from the browser. */
export const COUNTRIES =
  `AD AE AF AG AI AL AM AO AQ AR AS AT AU AW AX AZ BA BB BD BE BF BG BH BI BJ BL
BM BN BO BQ BR BS BT BV BW BY BZ CA CC CD CF CG CH CI CK CL CM CN CO CR CU CV CW CX CY CZ DE DJ
DK DM DO DZ EC EE EG EH ER ES ET FI FJ FK FM FO FR GA GB GD GE GF GG GH GI GL GM GN GP GQ GR GS
GT GU GW GY HK HM HN HR HT HU ID IE IL IM IN IO IQ IR IS IT JE JM JO JP KE KG KH KI KM KN KP KR
KW KY KZ LA LB LC LI LK LR LS LT LU LV LY MA MC MD ME MF MG MH MK ML MM MN MO MP MQ MR MS MT MU
MV MW MX MY MZ NA NC NE NF NG NI NL NO NP NR NU NZ OM PA PE PF PG PH PK PL PM PN PR PS PT PW PY
QA RE RO RS RU RW SA SB SC SD SE SG SH SI SJ SK SL SM SN SO SR SS ST SV SX SY SZ TC TD TF TG TH
TJ TK TL TM TN TO TR TT TV TW TZ UA UG UM US UY UZ VA VC VE VG VI VN VU WF WS YE YT ZA ZM ZW`
    .split(/\s+/)
    .filter(Boolean);

/**
 * The number and date formats offered. Common ones rather than every tag the
 * browser knows: a list of seven hundred is a list nobody finds their own in.
 */
export const LOCALES = [
  'ar-AE',
  'ar-SA',
  'cs-CZ',
  'da-DK',
  'de-AT',
  'de-CH',
  'de-DE',
  'el-GR',
  'en-AU',
  'en-CA',
  'en-GB',
  'en-IE',
  'en-IN',
  'en-NZ',
  'en-SG',
  'en-US',
  'en-ZA',
  'es-AR',
  'es-CL',
  'es-CO',
  'es-ES',
  'es-MX',
  'es-US',
  'fi-FI',
  'fr-BE',
  'fr-CA',
  'fr-CH',
  'fr-FR',
  'he-IL',
  'hi-IN',
  'hu-HU',
  'id-ID',
  'it-CH',
  'it-IT',
  'ja-JP',
  'ko-KR',
  'ms-MY',
  'nb-NO',
  'nl-BE',
  'nl-NL',
  'pl-PL',
  'pt-BR',
  'pt-PT',
  'ro-RO',
  'sv-SE',
  'th-TH',
  'tr-TR',
  'uk-UA',
  'vi-VN',
  'zh-CN',
  'zh-HK',
  'zh-TW',
];

export const CURRENCIES =
  `AED AFN ALL AMD ANG AOA ARS AUD AWG AZN BAM BBD BDT BGN BHD BIF BMD BND BOB BRL BSD BTN BWP
BYN BZD CAD CDF CHF CLP CNY COP CRC CUP CVE CZK DJF DKK DOP DZD EGP ERN ETB EUR FJD FKP GBP GEL
GHS GIP GMD GNF GTQ GYD HKD HNL HTG HUF IDR ILS INR IQD IRR ISK JMD JOD JPY KES KGS KHR KMF KPW
KRW KWD KYD KZT LAK LBP LKR LRD LSL LYD MAD MDL MGA MKD MMK MNT MOP MRU MUR MVR MWK MXN MYR MZN
NAD NGN NIO NOK NPR NZD OMR PAB PEN PGK PHP PKR PLN PYG QAR RON RSD RUB RWF SAR SBD SCR SDG SEK
SGD SHP SLE SOS SRD SSP STN SVC SYP SZL THB TJS TMT TND TOP TRY TTD TWD TZS UAH UGX USD UYU UZS
VES VND VUV WST XAF XCD XOF XPF YER ZAR ZMW ZWG`
    .split(/\s+/)
    .filter(Boolean);

export type Named = { code: string; name: string };

/** Codes with their names in the browser's language, sorted by name. */
export function named(codes: string[], type: 'region' | 'currency' | 'language'): Named[] {
  let names: Intl.DisplayNames | null = null;
  try {
    names = new Intl.DisplayNames(undefined, { type });
  } catch {
    // A browser without display names shows the codes, which still work.
  }
  return codes
    .map((code) => {
      let name = code;
      try {
        name = names?.of(code) ?? code;
      } catch {
        // An unknown code keeps its code as its name.
      }
      return { code, name };
    })
    .sort((a, b) => a.name.localeCompare(b.name));
}

/**
 * ISO 4217 active codes — the same list the server checks against
 * (`tenancy/region.py`). Not `Intl.supportedValuesOf`, which includes retired
 * currencies the server would refuse.
 */
export function supportedCurrencies(): string[] {
  return CURRENCIES;
}
